<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import { useGlobalStore } from '@/stores/GlobalStore'

/**
 * AppMenuPanel — outer shell for complex dropdown panels.
 *
 * Two modes:
 *
 *   1. Plain panel (default) — visual shell only.
 *      Slots: trigger ({ open }), default, footer ({ close }).
 *      Existing consumers (export menu, publish menu, etc.) sit here.
 *
 *   2. Applyable filter panel (opt-in via `applyable` prop) — adds a
 *      built-in staged-commit value model so each filter dropdown stops
 *      reimplementing the pending → apply → close dance.
 *
 *      Extra props: `value` (v-model:value, scalar or array), `multi`,
 *      `applyLabel`, `cancelLabel`.
 *
 *      Default slot receives `{ pending, toggle, isSelected }` so each
 *      option row wires itself with one click handler:
 *
 *        <AppMenuPanel v-model:open="open" v-model:value="sort" applyable>
 *          <template #trigger="{ open }">…</template>
 *          <template #default="{ toggle, isSelected }">
 *            <button v-for="o in options" @click="toggle(o.value)"
 *                    :class="{ 'bg-blue-50': isSelected(o.value) }">
 *              {{ o.label }}
 *            </button>
 *          </template>
 *        </AppMenuPanel>
 *
 *      Apply emits update:value with the staged value and closes.
 *      Cancel (and click-outside in applyable mode) discards pending and closes.
 *      Opening the panel resets pending from the current committed value.
 *
 *      Override the default footer by providing the `#footer` slot — you get
 *      `{ close, apply, cancel, pending }` to wire custom action rows.
 */
const props = defineProps({
  // ── Shell (existing) ─────────────────────────────────────────────────────
  open:       { type: Boolean, default: false },
  placement:  { type: String,  default: 'bottom-end' },
  width:      { type: String,  default: 'w-72' },
  extraClass: { type: String,  default: '' },
  panelStyle: { type: Object,  default: null },
  /**
   * Teleport the panel to <body> and position it `fixed` from the trigger's
   * rect. Use when the trigger lives inside a stacking context (a `sticky`/
   * `z-*` header, a `transform`ed ancestor) that would otherwise trap the
   * panel beneath fixed page chrome — the bottom pagination bar (`z-100`) or
   * the CDP support widget (`z-[9999]`). zIndex 10000 clears both. Mirrors
   * BaseListbox's `usePortal`.
   *
   * Portal mode also CLAMPS the panel to the viewport — horizontally (never
   * spills past an edge) and vertically (max-height = the space actually
   * available below/above the trigger, body scrolls past it). That keeps a long
   * panel off the fixed bottom-right widgets on short screens.
   */
  portal:     { type: Boolean, default: false },

  // ── Opt-in staged-commit mode ────────────────────────────────────────────
  applyable:   { type: Boolean, default: false },
  value:       { type: [String, Number, Array, Boolean, null], default: null },
  multi:       { type: Boolean, default: false },
  applyLabel:  { type: String,  default: 'Apply' },
  cancelLabel: { type: String,  default: 'Cancel' },
})

const emit = defineEmits(['update:open', 'update:value'])

// ── Staging state ───────────────────────────────────────────────────────────
const seedPending = () => props.multi
  ? (Array.isArray(props.value) ? [...props.value] : [])
  : props.value

const pending = ref(seedPending())

// ── Portal positioning ───────────────────────────────────────────────────────
const rootRef     = ref(null)
const panelRef    = ref(null)
const portalStyle = ref({})

const computePortalPosition = () => {
  const el = rootRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const gap = 4
  const MARGIN = 8
  // Floor for pathological cases (trigger flush against the viewport edge) —
  // a 20px-tall panel is less usable than one that slightly overhangs.
  const MIN_HEIGHT = 160
  const vw = window.innerWidth
  // zIndex 10000 sits above the pagination bar (z-100) and support widget (9999).
  const style = { position: 'fixed', zIndex: 10000 }
  if (props.placement.startsWith('top')) style.bottom = `${window.innerHeight - r.top + gap}px`
  else                                    style.top    = `${r.bottom + gap}px`

  // Anchor to the trigger, then CLAMP so the panel never spills past the
  // viewport — an off-screen fixed panel adds horizontal scroll and visibly
  // shifts the page when it opens (esp. wide mobile panels near a left/mid trigger).
  const panelWidth = panelRef.value?.offsetWidth || 0
  if (props.placement.endsWith('start')) {
    let left = r.left
    if (panelWidth) left = Math.min(left, vw - panelWidth - MARGIN)
    style.left = `${Math.max(left, MARGIN)}px`
  } else {
    let right = vw - r.right
    if (panelWidth) right = Math.min(right, vw - panelWidth - MARGIN)
    style.right = `${Math.max(right, MARGIN)}px`
  }

  // Clamp VERTICALLY too, for the same reason we clamp horizontally: a long
  // panel (the Columns checklist) on a short viewport ran past the bottom edge,
  // so its last rows were unreachable AND it collided with the fixed support /
  // referral bubbles parked in the bottom-right corner. Capping to the space
  // actually available turns the overflow into an internal scroll.
  const available = props.placement.startsWith('top')
    ? r.top - gap - MARGIN
    : window.innerHeight - r.bottom - gap - MARGIN
  style.maxHeight = `${Math.max(Math.round(available), MIN_HEIGHT)}px`

  portalStyle.value = style

  measureCornerOverlap()
}

// True once a max-height is actually in force (portal mode only).
const clamped = computed(() => !!portalStyle.value.maxHeight)

/**
 * The floating support + referral bubbles live in the bottom-right corner,
 * inside shadow-DOM hosts whose z-index is set by third-party scripts — so a
 * panel that reaches down there CANNOT reliably stack above them (see the
 * `batchPreviewOpen` note in GlobalStore). Instead we tell `useCdpSupport` to
 * hide them for as long as this panel overlaps that corner.
 *
 * Measured, not assumed: a short Sort dropdown never reaches the corner and so
 * never makes the bubbles blink out.
 *
 * Store write in a molecule follows the precedent AppPagination set with
 * `paginationBarHeight` — same widget-avoidance contract, same store.
 */
const globalStore = useGlobalStore()

// Widget cluster (support button + Cello badge + gaps), with headroom for the
// pagination bar that shifts them upward.
//   width : right offset 18 + support 48 + gap 24 + Cello 48 = ~138
//   height: bottom offset 18 (or bar 56 + gap 12 = 68) + button 48 = ~116
// Rounded up so a panel that stops just short of the cluster's top edge still
// counts — over-triggering costs a hidden bubble, under-triggering costs the
// user a covered panel, which is the failure being fixed.
const CORNER_W = 200
const CORNER_H = 170

const overlapsCorner = ref(false)

const measureCornerOverlap = async () => {
  await nextTick()

  const el = panelRef.value
  if (!props.open || !el) {
    overlapsCorner.value = false
    return
  }

  const r = el.getBoundingClientRect()
  overlapsCorner.value =
    r.bottom > window.innerHeight - CORNER_H && r.right > window.innerWidth - CORNER_W
}

// Ref-counted so two open overlays can't un-hide the widgets for each other.
const releaseCorner = () => {
  if (!overlapsCorner.value) return
  overlapsCorner.value = false
  globalStore.cornerOverlayCount = Math.max(globalStore.cornerOverlayCount - 1, 0)
}

watch(overlapsCorner, (over, wasOver) => {
  if (over === wasOver) return
  globalStore.cornerOverlayCount = Math.max(globalStore.cornerOverlayCount + (over ? 1 : -1), 0)
})

const onViewportChange = () => { if (props.open && props.portal) computePortalPosition() }

const teardownViewportListeners = () => {
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
}

// Reset pending whenever the panel opens — pending represents "what the user
// is staging this session", and we always start it from the current value.
// In portal mode also (re)compute the fixed position + track viewport changes.
watch(() => props.open, async (isOpen) => {
  if (isOpen) {
    pending.value = seedPending()
    if (props.portal) {
      await nextTick()
      computePortalPosition()
      window.addEventListener('resize', onViewportChange)
      window.addEventListener('scroll', onViewportChange, true)
    }
  } else {
    teardownViewportListeners()
    releaseCorner()
  }
})

onBeforeUnmount(() => {
  teardownViewportListeners()
  // Unmounting while open (route change) must not leave the widgets hidden.
  releaseCorner()
})

const isSelected = (v) => props.multi
  ? Array.isArray(pending.value) && pending.value.includes(v)
  : pending.value === v

const toggle = (v) => {
  if (props.multi) {
    if (!Array.isArray(pending.value)) pending.value = []
    const i = pending.value.indexOf(v)
    if (i >= 0) pending.value.splice(i, 1)
    else pending.value.push(v)
  } else {
    pending.value = v
  }
}

const close = () => emit('update:open', false)

const apply = () => {
  emit('update:value', props.multi ? [...pending.value] : pending.value)
  close()
}

const cancel = () => {
  // For non-applyable consumers this just closes — seedPending() returns the
  // (unused) initial value and no `update:value` is emitted.
  pending.value = seedPending()
  close()
}

const onOutsideClick = (event) => {
  if (rootRef.value?.contains(event.target)) return

  /**
   * A panel that isn't actually on screen doesn't get to close itself.
   *
   * Some consumers mount the SAME panel twice against one shared open-state —
   * e.g. `ImportCards` renders once in the desktop toolbar and again, with
   * `hideTrigger`, inside the mobile "···" menu. Only one of the two is
   * visible (the other sits under a `hidden`/`xl:hidden` wrapper), but both
   * were listening: opening from the visible trigger immediately tripped the
   * hidden twin's outside-click — the click is outside ITS root — and the menu
   * shut in the same tick.
   *
   * `display: none` collapses the box to 0×0, which `position: fixed`
   * (portal mode) doesn't affect — unlike `offsetParent`, which is null for
   * fixed elements and would misread a perfectly visible portal panel.
   */
  const rect = panelRef.value?.getBoundingClientRect()
  if (!rect?.width && !rect?.height) return

  cancel()
}

// ── Placement ───────────────────────────────────────────────────────────────
const placementClass = computed(() => {
  const placements = {
    'bottom-end':   'top-full right-0 mt-0.5',
    'bottom-start': 'top-full left-0 mt-0.5',
    'top-end':      'bottom-full right-0 mb-0.5',
    'top-start':    'bottom-full left-0 mb-0.5',
  }
  return placements[props.placement] ?? placements['bottom-end']
})
</script>

<template>
  <div class="relative" ref="rootRef">
    <!-- Trigger -->
    <slot name="trigger" :open="open" />

    <!-- Panel — teleported to body in portal mode so a stacking-context-bound
         trigger (sticky header, etc.) can't trap it under fixed page chrome. -->
    <Teleport to="body" :disabled="!portal">
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="open"
          ref="panelRef"
          v-click-outside="{ handler: onOutsideClick, events: ['click', 'touchstart'] }"
          :class="[
            (portal || panelStyle) ? 'fixed' : 'absolute',
            'z-[9999] bg-white border border-gray-200 rounded-lg overflow-hidden shadow-[0_2px_10px_rgba(9,38,67,0.12)]',
            // Only when a height cap is in play, so the footer stays pinned
            // while the body scrolls. Off by default — see the body's note.
            clamped ? 'flex flex-col' : '',
            (portal || panelStyle) ? '' : placementClass,
            width, extraClass
          ]"
          :style="portal ? portalStyle : (panelStyle || {})"
        >
        <!-- Body — exposes staging helpers when applyable.
             Scrolling is applied ONLY under a height cap: an overflow box clips
             absolutely-positioned descendants whether or not it ends up
             scrolling, which would cut off a nested dropdown opening past the
             panel's edge. Uncapped panels keep their old behaviour.

             `overflow-x-hidden` is not redundant: per CSS, when one axis is
             `visible` and the other isn't, the `visible` one computes to `auto`
             — so `overflow-y-auto` alone was ALSO producing a pointless
             horizontal scrollbar along the panel's bottom edge.

             The vertical bar is there because the content genuinely doesn't
             fit, but it's chrome inside a dropdown, so the thumb stays
             transparent until hover — the list still scrolls, it just doesn't
             read as a second scrollbar. -->
        <div
          class="flex flex-col"
          :class="clamped ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden app-menu-panel-scroll' : ''"
        >
          <slot :pending="pending" :toggle="toggle" :is-selected="isSelected" />
        </div>

        <!-- Footer: explicit slot wins; else auto-render when applyable -->
        <div
          v-if="$slots.footer"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <slot name="footer" :close="close" :apply="apply" :cancel="cancel" :pending="pending" />
        </div>
        <div
          v-else-if="applyable"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <AppButton variant="link" size="sm" @click="cancel">{{ cancelLabel }}</AppButton>
          <AppButton variant="default" size="sm" @click="apply">{{ applyLabel }}</AppButton>
        </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Slim, quiet scrollbar for the clamped panel body — visible on hover,
   invisible at rest so a capped dropdown doesn't read as a scroll region. */
.app-menu-panel-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.app-menu-panel-scroll:hover {
  scrollbar-color: #D1D5DB transparent;   /* gray-300 */
}
.app-menu-panel-scroll::-webkit-scrollbar {
  width: 6px;
}
.app-menu-panel-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 9999px;
}
.app-menu-panel-scroll:hover::-webkit-scrollbar-thumb {
  background-color: #D1D5DB;
}
</style>
